/**
 * Detects non-actionable errors thrown when a browser extension content
 * script cannot reach its background page / service worker.
 *
 * Common culprit:
 * - **Zotero Connector** — Safari/Chrome content script throws
 *   `Zotero Connector: Failed to send message <name> to background page. It may be dead.`
 *   when the extension's background page has been unloaded (MV3 service
 *   worker idle, Safari extension process killed).
 *
 * Other Safari Web Extensions use the same "to background page. It may be
 * dead" phrasing when `browser.runtime.sendMessage` fails after the
 * background page dies. The resulting error has no application stack frames
 * and is not actionable.
 *
 * Signature:
 * - message includes "to background page" and "it may be dead"
 * - stack contains no application source frames (typically just the error
 *   message itself)
 */
export function isBrowserExtensionBackgroundPageNoise(
  message: string | undefined,
  stack: string | undefined,
): boolean {
  if (!message) {
    return false;
  }

  const normalized = message.toLowerCase();
  const isBackgroundPageDead =
    normalized.includes("to background page") &&
    normalized.includes("it may be dead");

  if (!isBackgroundPageDead) {
    return false;
  }

  if (!stack) {
    return true;
  }

  const frames = stack
    .split("\n")
    .filter((line) => line.trim().startsWith("at "));

  if (frames.length === 0) {
    return true;
  }

  return frames.every((frame) => !_isApplicationSourceFrame(frame));
}

function _isApplicationSourceFrame(frame: string): boolean {
  return (
    frame.includes("/src/") ||
    frame.includes("/app/") ||
    frame.includes("/lib/") ||
    frame.includes("/components/") ||
    frame.includes("webpack://_N_E/./src/")
  );
}
